import { Stack } from 'component-library';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataWithBenchmarks } from '@/components/Charts/utils';
import { WaterHoldingScenarioChart } from '@/components/Charts/WaterHoldingCapacityScenario';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useBenchmarksForOrganization, useBenchmarksForPlot } from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';

import { useIsLandPortionProject } from '../../hooks/useIsLandPortionProject';

export const WaterGraphTile = () => {
  const isLandPortionProject = useIsLandPortionProject();
  return isLandPortionProject ? <ProjectWaterGraph /> : <PlotWaterGraph />;
};

export const ProjectWaterGraph = () => {
  const { ...filters } = useWatch();
  const { analysisType } = useNCCardContext();

  const historicWaterAndSoilMoistureFact =
    analysisType === 'total'
      ? IndicatorName.water_and_soil_moisture_historic_total_graph
      : IndicatorName.water_and_soil_moisture_historic_per_ha_graph;
  const { [historicWaterAndSoilMoistureFact]: historicWaterAndSoilMoisture } = useOrganizationIndicatorGraphFact<
    GraphFact[]
  >(historicWaterAndSoilMoistureFact);
  const whcHistoricData = historicWaterAndSoilMoisture?.value ?? [];

  const waterHoldingCapacity10yearUpliftPotentialFact =
    analysisType === 'total'
      ? IndicatorName.water_fc_uplift_potential_total_graph
      : IndicatorName.water_fc_uplift_potential_per_ha_graph;
  const { [waterHoldingCapacity10yearUpliftPotentialFact]: waterHoldingCapacity10yearUpliftPotential } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(waterHoldingCapacity10yearUpliftPotentialFact, null, filters);
  const whcData = waterHoldingCapacity10yearUpliftPotential?.value;

  const benchmarkFactType =
    analysisType === 'total' ? IndicatorName.water_fc_total_benchmark : IndicatorName.water_fc_per_ha_benchmark;

  const { [benchmarkFactType]: whcLowBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
    },
  );

  const { [benchmarkFactType]: whcModerateBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate },
  );

  const { [benchmarkFactType]: whcHighBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    },
  );

  const benchmarks = useBenchmarksForOrganization(benchmarkFactType);

  /**
   * We only null check for whcData since whcData is the primary source data of the graph
   * wpData and other data are reference lines that are displayed only when whcData is rendered
   */
  const noChartData = !whcData?.length;
  if (noChartData) {
    return <NoData />;
  }

  const chartData = [
    ...whcHistoricData,
    ...whcData,
    ...getChartDataWithBenchmarks({
      chartData: whcData,
      benchmarks: { low: whcLowBenchmark, moderate: whcModerateBenchmark, high: whcHighBenchmark },
    }),
  ];

  return <WaterGraph chartData={chartData} benchmarks={benchmarks} />;
};

export const PlotWaterGraph = () => {
  const { analysisType } = useNCCardContext();
  const { landPortionId: plotId, ...filters } = useWatch();

  const historicWaterFactType =
    analysisType === 'total'
      ? IndicatorName.water_and_soil_moisture_historic_total_graph
      : IndicatorName.water_and_soil_moisture_historic_per_ha_graph;

  const whcUpliftPotentialFactType =
    analysisType === 'total'
      ? IndicatorName.water_fc_uplift_potential_total_graph
      : IndicatorName.water_fc_uplift_potential_per_ha_graph;

  const whcBenchmarkFactType =
    analysisType === 'total' ? IndicatorName.water_fc_total_benchmark : IndicatorName.water_fc_per_ha_benchmark;

  const facts = usePlotFacts([historicWaterFactType, whcBenchmarkFactType], plotId);
  const filteredFacts = usePlotFacts<GraphFact[]>([whcUpliftPotentialFactType], plotId, filters);

  const historicWaterAndSoilMoisture = getFactFromFactArray<GraphFact[]>(facts, historicWaterFactType);
  const whcHistoricData = historicWaterAndSoilMoisture?.value ?? [];

  const waterHoldingCapacity10yearUpliftPotential = getFactFromFactArray<GraphFact[]>(
    filteredFacts,
    whcUpliftPotentialFactType,
  );
  const whcData = waterHoldingCapacity10yearUpliftPotential?.value;

  const whcLowBenchmark = getFactFromFactArray<number>(facts, whcBenchmarkFactType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });
  const whcModerateBenchmark = getFactFromFactArray<number>(facts, whcBenchmarkFactType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });
  const whcHighBenchmark = getFactFromFactArray<number>(facts, whcBenchmarkFactType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });

  const benchmarks = useBenchmarksForPlot(whcBenchmarkFactType, plotId);

  /**
   * We only null check for whcData since whcData is the primary source data of the graph
   * wpData and other data are reference lines that are displayed only when whcData is rendered
   */
  const noChartData = !whcData?.length;
  if (noChartData) {
    return <NoData />;
  }

  const chartData = [
    ...whcHistoricData,
    ...whcData,
    ...getChartDataWithBenchmarks({
      chartData: whcData,
      benchmarks: { low: whcLowBenchmark, moderate: whcModerateBenchmark, high: whcHighBenchmark },
    }),
  ];

  return <WaterGraph chartData={chartData} benchmarks={benchmarks} />;
};

export const WaterGraph = ({
  chartData,
  benchmarks,
}: {
  chartData: GraphFact[];
  benchmarks: (number | undefined)[];
}) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();
  const { timeFrame } = useWatch();

  const { zoom, setZoom } = useChartZoom();

  return (
    <Stack center data-testid='water-uplift-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'>
        <ChartHeading
          heading={t('shared.projects.project.impactProjections.water.labels.graphHeadline', {
            timeFrame,
          })}
          popover={{
            title: t('shared.ncaDetail.details.water.explainers.upliftPotential.title'),
            body: t('shared.ncaDetail.details.water.explainers.upliftPotential.body'),
          }}
          className='w-full px-6 sm:px-0'
          zoom={zoom}
          setZoom={setZoom}
        />
        <WaterHoldingScenarioChart
          analysisType={analysisType}
          data={chartData}
          benchmarks={benchmarks}
          timeFrame={Number(timeFrame)}
          showTooltip={true}
          className='relative'
          data-testid='water-holding-capacity-chart'
          zoom={zoom}
          setZoom={setZoom}
        />
      </Stack>
    </Stack>
  );
};
